import { tenant } from '@tenant';

import type {
  Cancellations,
  CancellationsInitiatorType,
  PayoutReview
} from 'api';

import { ActivityBadge } from './ActivityBadge';

type ActivityDetailProps = {
  createdAt: string;
  createdBy: string | null;
  source: string | null;
  batchId: number | null;
  cancellation: Cancellations | null;
  reviews?: PayoutReview[] | null;
};

type Source = 'api' | 'csv' | 'manual' | 'unknown' | 'iso_20022';
type Initiator = CancellationsInitiatorType | 'unknown';

const normalizeSource = (
  s: string | null | undefined,
  batchId: string | null
): Source =>
  ['api', 'csv', 'manual', 'iso_20022'].includes(String(s))
    ? (s as Source)
    : batchId
      ? 'csv'
      : 'unknown';
const normalizeInitiator = (
  i: CancellationsInitiatorType | null | undefined
): Initiator => i ?? 'unknown';

const SOURCE_META = {
  api: {
    name: 'API',
    kind: 'apiCreated' as const,
    message:
      "This payout was created using your organisation's API integration."
  },
  csv: {
    name: 'Batch',
    kind: 'batchCreated' as const,
    message: 'This payout was created using batch upload.'
  },
  iso_20022: {
    name: 'Batch',
    kind: 'batchCreated' as const,
    message: 'This payout was created using batch upload.'
  },
  manual: {
    name: 'Customer App user',
    kind: 'userCreated' as const,
    message: 'This payout was created from Customer App.'
  },
  unknown: {
    name: '',
    kind: 'unknownCreated' as const,
    message: 'We have no information on how this payout was created.'
  }
} satisfies Record<Source, { name: string; kind: string; message: string }>;

const INITIATOR_META = {
  'Customer::User': {
    name: 'Customer',
    message: 'This payout was cancelled from Customer App.'
  },
  'Admin::User': {
    name: tenant.name.company,
    message: `This payout was manually cancelled by ${tenant.name.company}.`
  },
  'Customer::Organisation': {
    name: 'API',
    message:
      "This payout was cancelled via your organisation's API integration."
  },
  unknown: {
    name: 'Unknown',
    message: 'Unfortunately we cannot show how this payout was cancelled.'
  }
} as const satisfies Record<Initiator, { name: string; message: string }>;

export const ActivityDetails = ({
  createdAt,
  createdBy,
  source,
  batchId,
  cancellation,
  reviews
}: ActivityDetailProps) => {
  const requestSource = normalizeSource(source, String(batchId));
  const creatorName = createdBy ?? SOURCE_META[requestSource].name;
  const creatorMessage = SOURCE_META[requestSource].message;
  const activityKind = SOURCE_META[requestSource].kind;

  const initiatorType = normalizeInitiator(cancellation?.initiator_type);
  const cancellationActorName =
    cancellation?.initiator_name || INITIATOR_META[initiatorType].name;
  const cancellationMessage = INITIATOR_META[initiatorType].message;

  const showCreatorBadge = Boolean(createdAt);
  const showCancellationBadge = Boolean(cancellation?.created_at);
  const showReviewBadge = Boolean(reviews?.length);

  return (
    <div className="break-words pb-6">
      <p className="pb-2 text-base text-content-secondary">Activities</p>
      <div className="flex flex-wrap gap-2">
        {showCreatorBadge && (
          <ActivityBadge
            kind={activityKind}
            name={creatorName}
            message={creatorMessage}
            timestamp={createdAt}
          />
        )}

        {showCancellationBadge && (
          <ActivityBadge
            kind="cancelled"
            name={cancellationActorName}
            message={cancellationMessage}
            timestamp={cancellation?.created_at}
          />
        )}

        {showReviewBadge &&
          reviews?.map((review, idx) => (
            <ActivityBadge
              key={idx}
              kind="approved"
              name={review.reviewer_name ?? 'Unknown'}
              message="This payout was approved from Customer App."
              timestamp={review.created_at ?? ''}
            />
          ))}
      </div>
    </div>
  );
};
